import { useState } from "react";
import { ChevronDown, ChevronUp, Copy, Check, Lightbulb, MapPin, Users, GitBranch, Eye, AlertTriangle, ShieldAlert, HelpCircle } from "lucide-react";
import { Badge } from "../../ui/badge";

const Section = ({ icon: Icon, label, children }) => (
  <div className="mt-3">
    <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-400 mb-1">
      <Icon className="w-3 h-3" /> {label}
    </p>
    <div className="text-xs text-slate-300 leading-relaxed">{children}</div>
  </div>
);

const asList = (v) => (Array.isArray(v) ? v : v ? [String(v)] : []);

const List = ({ items }) =>
  Array.isArray(items) && items.length > 0 ? (
    <ul className="space-y-1">
      {items.map((it, i) => (
        <li key={i} className="flex gap-1.5">
          <span className="text-slate-500">•</span>
          <span>{typeof it === "string" ? it : `${it.where ? it.where + " — " : ""}${it.what || ""}`}</span>
        </li>
      ))}
    </ul>
  ) : null;

const proposalToText = (p) => {
  const lines = [
    `IDEA: ${p.title}`,
    `Core idea: ${p.core_idea}`,
    `Why it works: ${p.why_it_works}`,
    `Placement: ${p.recommended_placement}`,
  ];
  const crumbs = asList(p.setup_breadcrumbs);
  if (crumbs.length) {
    lines.push("Setup / breadcrumbs:");
    crumbs.forEach((b) => lines.push(`  - ${b.where ? b.where + ": " : ""}${b.what || b}`));
  }
  if (p.affected_chapters) lines.push(`Affected: ${p.affected_chapters}`);
  if (p.reader_knowledge) lines.push(`Reader knowledge: ${p.reader_knowledge}`);
  if (p.character_knowledge) lines.push(`Character knowledge: ${p.character_knowledge}`);
  const risks = asList(p.risks);
  if (risks.length) lines.push(`Risks: ${risks.join("; ")}`);
  return lines.join("\n");
};

const ProposalCard = ({ proposal: p, index }) => {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const copy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(proposalToText(p));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  const hasCanonConcern = asList(p.canon_concerns).length > 0;

  return (
    <div
      className="rounded-xl border border-indigo-500/25 bg-slate-900/70 p-4 transition-colors hover:border-indigo-400/40"
      data-testid={`architect-proposal-card-${index}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <Badge className="bg-amber-900/50 text-amber-300 border-amber-500/40 text-[10px] mb-2" data-testid={`architect-idea-badge-${index}`}>
            <Lightbulb className="w-3 h-3 mr-1" /> IDEA — Not yet part of your story
          </Badge>
          <h4 className="text-sm font-semibold text-slate-100">{index + 1}. {p.title}</h4>
        </div>
        <button
          onClick={copy}
          className="shrink-0 p-1.5 rounded text-slate-400 hover:text-slate-200 hover:bg-slate-700/50"
          title="Copy this idea"
          data-testid={`architect-copy-proposal-${index}`}
        >
          {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>

      <p className="mt-2 text-xs text-slate-300 leading-relaxed">{p.core_idea}</p>
      <Section icon={MapPin} label="Recommended placement">{p.recommended_placement}</Section>
      {hasCanonConcern && (
        <p className="mt-2 flex items-start gap-1.5 text-[11px] text-rose-300/90">
          <ShieldAlert className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          Possible canon concern: {asList(p.canon_concerns).join("; ")}
        </p>
      )}

      <button
        onClick={() => setExpanded(!expanded)}
        className="mt-3 flex items-center gap-1 text-[11px] text-indigo-300 hover:text-indigo-200"
        data-testid={`architect-expand-proposal-${index}`}
      >
        {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        {expanded ? "Show less" : "Show full plan"}
      </button>

      {expanded && (
        <div className="mt-1 border-t border-slate-700/50 pt-1">
          <Section icon={Lightbulb} label="Why it works">{p.why_it_works}</Section>
          {asList(p.setup_breadcrumbs).length > 0 && (
            <Section icon={GitBranch} label="Setup & breadcrumbs"><List items={asList(p.setup_breadcrumbs)} /></Section>
          )}
          {p.affected_chapters && <Section icon={MapPin} label="Affected chapters">{p.affected_chapters}</Section>}
          {asList(p.characters_affected).length > 0 && (
            <Section icon={Users} label="Characters affected">{asList(p.characters_affected).join(", ")}</Section>
          )}
          {asList(p.relationships_affected).length > 0 && (
            <Section icon={Users} label="Relationships affected"><List items={asList(p.relationships_affected)} /></Section>
          )}
          {asList(p.plot_threads_affected).length > 0 && (
            <Section icon={GitBranch} label="Plot threads affected"><List items={asList(p.plot_threads_affected)} /></Section>
          )}
          {p.reader_knowledge && <Section icon={Eye} label="Reader knowledge">{p.reader_knowledge}</Section>}
          {p.character_knowledge && <Section icon={Eye} label="Character knowledge">{p.character_knowledge}</Section>}
          {p.setup_payoff && <Section icon={GitBranch} label="Setup / payoff">{p.setup_payoff}</Section>}
          {p.tone_effect && <Section icon={Lightbulb} label="Tone & emotional effect">{p.tone_effect}</Section>}
          {asList(p.risks).length > 0 && <Section icon={AlertTriangle} label="Risks"><List items={asList(p.risks)} /></Section>}
          {asList(p.possible_discrepancies).length > 0 && (
            <Section icon={AlertTriangle} label="Possible discrepancies noticed"><List items={asList(p.possible_discrepancies)} /></Section>
          )}
          {asList(p.assumptions).length > 0 && (
            <Section icon={HelpCircle} label="Assumptions made"><List items={asList(p.assumptions)} /></Section>
          )}
        </div>
      )}
    </div>
  );
};

export const ProposalCards = ({ proposals }) => (
  <div className="space-y-3" data-testid="architect-proposals-list">
    {proposals.map((p, i) => (
      <ProposalCard key={i} proposal={p} index={i} />
    ))}
  </div>
);
